import { Route } from 'vue-router';
import Amplify, { Auth, Hub } from 'aws-amplify';
import { AwsCognitoOAuthOpts } from '@aws-amplify/auth/lib/types';
import { CognitoUserSession } from 'amazon-cognito-identity-js';
import router from '@/router';
import store from '@/store/index';
import config from '@/config';
import { User } from '@/models/auth';

// Configure the global settings
Amplify.configure({
  Auth: {
    region: config.cognito.region,
    userPoolId: config.cognito.userPoolId,
    userPoolWebClientId: config.cognito.userPoolWebClientId,
    oauth: {
      domain: config.cognito.oauthDomain,
      scope: [
        'phone',
        'email',
        'profile',
        'openid',
        'aws.cognito.signin.user.admin'
      ],
      redirectSignIn: `${config.clientUrl}/authenticated/`,
      redirectSignOut: `${config.clientUrl}/logout/`,
      responseType: 'token'
    }
  }
});

// Getters
export async function getToken(integrityCheck: boolean = true) {
  try {
    const appUser = store.getters['auth/user'];
    const session = await Auth.currentSession();
    if (integrityCheck && !compareAppUserSessionIntegrity(appUser, session)) {
      throw new Error('Authentication integrity error.');
    }
    return session.getAccessToken().getJwtToken();
  } catch (e) {
    return null;
  }
}

export async function getUser(): Promise<User | null> {
  try {
    const user = await Auth.currentAuthenticatedUser();
    const tokenPayload = user.signInUserSession.getIdToken().payload;
    const securityRole = tokenPayload['cognito:groups']
      ? tokenPayload['cognito:groups'][0]
      : 'user';
    const userIAMId = tokenPayload.sub;
    return {
      ...user.attributes,
      securityRole,
      iamId: userIAMId
    };
  } catch (e) {
    return null;
  }
}

// Auth actions

export function registerAuthListener() {
  Hub.listen('auth', async data => {
    switch (data.payload.event) {
      case 'signIn':
        await logIn();
        break;
      default:
    }
  });
}

export function openHostedUi() {
  const authConfig = Auth.configure({});
  const {
    domain,
    redirectSignIn,
    responseType
  } = authConfig.oauth as AwsCognitoOAuthOpts;
  const clientId = authConfig.userPoolWebClientId;
  const url = `https://${domain}/login?redirect_uri=${redirectSignIn}&response_type=${responseType}&client_id=${clientId}`;

  // Redirect to the hosted UI
  window.location.assign(url);
}

export async function logIn() {
  const session = await Auth.currentSession();
  if (!session.isValid()) return;

  const { payload } = session.getIdToken();

  if (payload.user_sync_failed) {
    // If the token claim 'user_sync_failed' is set it means that the Lambda trigger responsible
    // for syncing the Cognito Userpool and AUT Store DB failed. In that case the integrity
    // of the user data can't be guaranteed so we should fail the login and display an error
    await store.dispatch(
      'auth/logInFailure',
      new Error('There was an error signing you in. Please try again later.')
    );
    return;
  }

  if (payload.user_group_mapping_failed) {
    // If the token claim 'user_group_mapping_failed' is set it means that the Lambda trigger
    // responsible for mapping the user to an user group failed.
    await store.dispatch(
      'auth/logInFailure',
      new Error(
        'User is not in the allowed groups. Please request group membership.'
      )
    );
    return;
  }

  await store.dispatch('auth/logIn');
  const user = store.getters['auth/user'];
  // Only redirect from login page if the user was successfully fetched and
  // so the logIn action succeeded. Otherwise the Authenticated view will display
  // an error and allows the user to repeat login
  if (user) {
    redirectFromLoginPage();
  }
}

export async function logOut(returnRoute?: Route) {
  if (!returnRoute) returnRoute = router.currentRoute;
  store.dispatch('auth/setLoginReturnPath', returnRoute);
  store.dispatch('auth/logOut');
  await Auth.signOut();
}

// Router helpers
export function redirectFromLoginPage() {
  const returnRoute = store.getters['auth/loginReturnPath'] || 'PlantList';
  router.push(returnRoute);
  store.dispatch('auth/clearLoginReturnPath');
}

// Utils
function compareAppUserSessionIntegrity(
  appUser: User,
  currentSession: CognitoUserSession
) {
  const sessionIAMId = currentSession.getIdToken().decodePayload().sub;
  return appUser && appUser.iamId === sessionIAMId;
}
